import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import styled, { keyframes } from 'styled-components';

const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(10px); }
  to { opacity: 1; transform: translateY(0); }
`;

const LoadingContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 100vh;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  animation: ${fadeIn} 0.5s ease-out;
`;

const LoadingSpinner = styled.div`
  width: 40px;
  height: 40px;
  border: 4px solid rgba(255, 255, 255, 0.3);
  border-radius: 50%;
  border-top-color: white;
  animation: spin 1s ease-in-out infinite;
  margin-bottom: 20px;

  @keyframes spin {
    to { transform: rotate(360deg); }
  }
`;

const LoadingText = styled.p`
  font-size: 1.1rem;
  margin: 0;
  opacity: 0.9;
`;

const ProtectedRoute = ({ children, requiredRole = null, fallback = null }) => {
  const { isAuthenticated, loading, user } = useAuth();
  const location = useLocation();

  // Show loading spinner while checking authentication
  if (loading) {
    return (
      <LoadingContainer>
        <LoadingSpinner />
        <LoadingText>Verificando autenticação...</LoadingText>
      </LoadingContainer>
    );
  }

  // If not authenticated, redirect to login with the current location
  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // If a specific role is required and user doesn't have it
  if (requiredRole && user?.role !== requiredRole) {
    // Check if user has the required role in their roles array (for multi-role users)
    const hasRequiredRole = user?.roles && Array.isArray(user.roles) 
      ? user.roles.includes(requiredRole) 
      : false;

    if (!hasRequiredRole) {
      // If a fallback component is provided, show it instead of redirecting
      if (fallback) {
        return fallback;
      }

      // Otherwise, redirect to dashboard with an access denied message
      return (
        <Navigate 
          to="/dashboard" 
          state={{ 
            from: location, 
            error: 'Você não tem permissão para acessar esta página.' 
          }} 
          replace 
        />
      );
    }
  }

  // User is authenticated and has the required role (if any)
  return children;
};

export default ProtectedRoute;